import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import appAPI from '../../api/api'
import { SkillsPassportResults, TeamResults, UserResults } from '../../api/model/models'

interface TInitialState {
  ratingResults: {
    loadingPersonalPlaces: boolean
    loadingTeamPlaces: boolean
    prizePlacesPersonal: SkillsPassportResults[]
    otherPlacesPersonal: SkillsPassportResults[]
    prizePlacesTeam: SkillsPassportResults[]
    otherPlacesTeam: SkillsPassportResults[]
    personalCount: number | undefined
    teamCount: number | undefined
  }
  ratingResultDetail: {
    loading: boolean
    data: TeamResults | undefined
    currentRequestId: string | undefined
    preparingForDownloadingResultsDetail: {
      openedParentCollapse: boolean
      openedChildrenCollapse: boolean
    }
  }
  ratingResultsUser: {
    allDate: string[]
    data: UserResults[]
    loading: boolean
    preparingForDownloadingResultsUser: {
      openedParentCollapse: boolean
      openedChildrenCollapse: boolean
    }
  }
}

const initialState: TInitialState = {
  ratingResults: {
    loadingPersonalPlaces: false,
    loadingTeamPlaces: false,
    prizePlacesPersonal: [],
    otherPlacesPersonal: [],
    prizePlacesTeam: [],
    otherPlacesTeam: [],
    personalCount: undefined,
    teamCount: undefined,
  },
  ratingResultDetail: {
    loading: false,
    data: undefined,
    currentRequestId: undefined,
    preparingForDownloadingResultsDetail: {
      openedParentCollapse: false,
      openedChildrenCollapse: false,
    },
  },
  ratingResultsUser: {
    allDate: [],
    data: [],
    loading: false,
    preparingForDownloadingResultsUser: {
      openedParentCollapse: false,
      openedChildrenCollapse: false,
    },
  },
}

const getRatingResultsPersonal = createAsyncThunk(
  'getRatingResultsPersonal',
  async (props: { championshipId: string | number; competenceId: string | number; manyParticipants: boolean }) => {
    return await appAPI.skillsPassport.getRatingResults(
      props.championshipId,
      props.competenceId,
      props.manyParticipants,
    )
  },
)

const getRatingResultsTeam = createAsyncThunk(
  'getRatingResultsTeam',
  async (props: { championshipId: string | number; competenceId: string | number; manyParticipants: boolean }) => {
    return await appAPI.skillsPassport.getRatingResults(
      props.championshipId,
      props.competenceId,
      props.manyParticipants,
    )
  },
)

const getDetailRatingResults = createAsyncThunk('getDetailRatingResults', async (teamId: number) => {
  return await appAPI.skillsPassport.getDetailRatingResults(teamId)
})

const getRatingResultsUser = createAsyncThunk('getRatingResultsUser', async (userId: string | number) => {
  return await appAPI.skillsPassport.getRatingResultsUser(userId)
})

const ratingResultsSlice = createSlice({
  name: 'ratingResults',
  initialState: initialState,
  reducers: {
    clearRatingResultDetail(state) {
      state.ratingResultDetail.data = undefined
      state.ratingResultDetail.loading = false
      state.ratingResultDetail.currentRequestId = undefined
    },
    setPreparingForDownloadingResultsDetail(
      state,
      { payload }: PayloadAction<Partial<{ openedParentCollapse: boolean; openedChildrenCollapse: boolean }>>,
    ) {
      if (payload.openedParentCollapse !== undefined)
        state.ratingResultDetail.preparingForDownloadingResultsDetail.openedParentCollapse =
          payload.openedParentCollapse
      if (payload.openedChildrenCollapse !== undefined)
        state.ratingResultDetail.preparingForDownloadingResultsDetail.openedChildrenCollapse =
          payload.openedChildrenCollapse
    },
    setPreparingForDownloadingResultsUser(
      state,
      { payload }: PayloadAction<Partial<{ openedParentCollapse: boolean; openedChildrenCollapse: boolean }>>,
    ) {
      if (payload.openedParentCollapse !== undefined)
        state.ratingResultsUser.preparingForDownloadingResultsUser.openedParentCollapse = payload.openedParentCollapse
      if (payload.openedChildrenCollapse !== undefined)
        state.ratingResultsUser.preparingForDownloadingResultsUser.openedChildrenCollapse =
          payload.openedChildrenCollapse
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getRatingResultsPersonal.pending, (state) => {
        state.ratingResults.loadingPersonalPlaces = true
      })
      .addCase(getRatingResultsPersonal.fulfilled, (state, { payload }) => {
        state.ratingResults.personalCount = payload.data.length
        const sortedData = payload.data.sort((a, b) => +a.place - +b.place)
        state.ratingResults.prizePlacesPersonal = sortedData.slice(0, 3)
        state.ratingResults.otherPlacesPersonal = sortedData.slice(3)
        state.ratingResults.loadingPersonalPlaces = false
      })
      .addCase(getRatingResultsPersonal.rejected, (state) => {
        state.ratingResults.prizePlacesPersonal = []
        state.ratingResults.otherPlacesPersonal = []
        state.ratingResults.loadingPersonalPlaces = false
      })

      .addCase(getRatingResultsTeam.pending, (state) => {
        state.ratingResults.loadingTeamPlaces = true
      })
      .addCase(getRatingResultsTeam.fulfilled, (state, { payload }) => {
        state.ratingResults.teamCount = payload.data.length
        const sortedData = payload.data.sort((a, b) => +a.place - +b.place)
        state.ratingResults.prizePlacesTeam = sortedData.slice(0, 3)
        state.ratingResults.otherPlacesTeam = sortedData.slice(3)
        state.ratingResults.loadingTeamPlaces = false
      })
      .addCase(getRatingResultsTeam.rejected, (state) => {
        state.ratingResults.prizePlacesTeam = []
        state.ratingResults.otherPlacesTeam = []
        state.ratingResults.loadingTeamPlaces = false
      })

      .addCase(getDetailRatingResults.pending, (state, action) => {
        state.ratingResultDetail.loading = true
        state.ratingResultDetail.currentRequestId = action.meta.requestId
      })
      .addCase(getDetailRatingResults.fulfilled, (state, { payload, meta }) => {
        if (state.ratingResultDetail.currentRequestId !== meta.requestId) return

        state.ratingResultDetail.data = payload.data
        state.ratingResultDetail.loading = false
        state.ratingResultDetail.currentRequestId = undefined
      })
      .addCase(getDetailRatingResults.rejected, (state, { meta }) => {
        if (state.ratingResultDetail.currentRequestId !== meta.requestId) return

        state.ratingResultDetail.loading = false
        state.ratingResultDetail.currentRequestId = undefined
      })

      .addCase(getRatingResultsUser.pending, (state) => {
        state.ratingResultsUser.loading = true
      })
      .addCase(getRatingResultsUser.fulfilled, (state, { payload }) => {
        const makeArr: string[] = []

        const allDate = payload.data
          .map((item) => {
            const res = item.championshipStartDate.slice(0, 4)
            const someInArr = makeArr.some((item) => item === res)

            if (!someInArr) {
              makeArr.push(res)
            } else {
              return undefined
            }

            return res
          })
          .filter((item) => item !== undefined) as string[]

        allDate.sort((a, b) => +b - +a)

        state.ratingResultsUser.allDate = allDate
        state.ratingResultsUser.data = payload.data
        state.ratingResultsUser.loading = false
      })
      .addCase(getRatingResultsUser.rejected, (state) => {
        state.ratingResultsUser.loading = false
      })
  },
})

export default ratingResultsSlice.reducer

export const ratingResultsSliceActions = {
  ...ratingResultsSlice.actions,
  getRatingResultsPersonal,
  getRatingResultsTeam,
  getDetailRatingResults,
  getRatingResultsUser,
}
